import { MAX_PAGE_SIZE } from '@aysar/contracts';
import { useCallback, useMemo } from 'react';

import { useResolveProductKeys } from '@/shared/services/subscription-product-lookup/subscription-product-lookup.api';

export interface UseProductLabels {
  /** The plan's name, or the key itself when the catalogue no longer holds it. */
  labelOf: (key: string) => string;
  isResolving: boolean;
}

/**
 * Turns the product **keys** on a page of orders into the names an operator
 * reads.
 *
 * One request for the whole page, not one per row: the shared lookup takes a
 * comma-joined `keys` filter (P23b2's api change), which is the alternative to
 * the N+1 P22b4 recorded and to the `pageSize=100` preload W6 forbids by name.
 *
 * ## A withdrawn plan keeps its key, and that is the right answer
 *
 * An order is a financial record and outlives the plan it bought. The lookup
 * answers only what the catalogue still holds, so a key it cannot resolve falls
 * back to **itself** — the operator sees `premium.monthly` rather than a blank
 * cell, and the row stays something they can search for and talk about.
 */
export function useProductLabels(keys: readonly string[]): UseProductLabels {
  // Keyed on the CONTENT, not the array's identity: the table rebuilds `keys` on
  // every render, and a memo watching the array would re-fire the resolve on
  // every keystroke in a filter. Extracted to a variable because the lint rule
  // requires a dependency list of simple expressions — which is also what makes
  // the intent readable.
  const signature = keys.join(',');
  // Deduplicated and capped: the resolve filter is `.max(MAX_PAGE_SIZE)` and the
  // api REFUSES an over-large set rather than trimming it (N7), so a page whose
  // rows all name different plans must not be able to 422 the whole column.
  const wanted = useMemo(
    () => [...new Set(signature.split(',').filter((key) => key !== ''))].slice(0, MAX_PAGE_SIZE),
    [signature],
  );

  const { items, isFetching } = useResolveProductKeys(wanted);

  const byKey = useMemo(() => new Map(items.map((item) => [item.value, item.label])), [items]);

  const labelOf = useCallback((key: string) => byKey.get(key) ?? key, [byKey]);

  return { labelOf, isResolving: isFetching };
}
